import menuStore from './localForageInstance';

// A small read-through cache for the data the Deal and Half n Half sheets load every time they open
// (deal data about 33 KB, the half-and-half configuration, the pizzas for a size, a product's recipe).
// The menu itself is already kept on the device (see syncMenu.js); this is the same idea for the rest.
//
// - Inside its age limit a stored answer is returned straight away, and the call is repeated in the
//   background so that the NEXT open has fresh data (the sheet that is open is never changed under the
//   person using it).
// - Past the age limit, or with nothing stored, the call is made and awaited, exactly as before.
// - "Sync Menu" clears it, and an entry stored by another login or restaurant is never used.
// - The Instant cart switch on the Settings page (localStorage cart_optimistic = '0') turns it off:
//   every call then goes to the server as before.

const PREFIX = 'cache_';
const DEFAULT_MAX_AGE_MS = 10 * 60 * 1000;

const memory = new Map();
const refreshing = new Set();

export const dataCacheEnabled = () => {
  try {
    return window.localStorage.getItem('cart_optimistic') !== '0';
  } catch {
    return true;
  }
};

// Who the entries belong to: a different login or restaurant on the same device starts empty.
const scope = () => {
  try {
    return `${window.localStorage.getItem('userId') || ''}|${window.localStorage.getItem('restaurant_name') || ''}`;
  } catch {
    return '';
  }
};

const readEntry = async (key) => {
  const k = PREFIX + key;
  let entry = memory.get(k);
  if (!entry) {
    try {
      entry = await menuStore.getItem(k);
    } catch {
      entry = null;
    }
    if (entry) memory.set(k, entry);
  }
  return entry;
};

const writeEntry = async (key, data) => {
  const k = PREFIX + key;
  const entry = { t: Date.now(), scope: scope(), data };
  memory.set(k, entry);
  try {
    await menuStore.setItem(k, entry);
  } catch {
    // storage full or blocked: the in-memory copy still serves this session
  }
};

// An answer worth keeping: HTTP 200 with a body that is not the API's own "no" (status 0 / false).
const defaultAccept = (res) => !!res && res.status === 200 && res.data != null && res.data.status !== 0 && res.data.status !== false;

/**
 * `fetcher` is the original call (it resolves with the HTTP response). Resolves with a response of the
 * same shape: the stored one ({ data, status: 200, fromCache: true }) or the live one.
 */
export async function cachedCall(key, fetcher, { maxAgeMs = DEFAULT_MAX_AGE_MS, accept = defaultAccept } = {}) {
  if (!dataCacheEnabled()) return fetcher();

  const entry = await readEntry(key);
  if (entry && entry.scope === scope() && Date.now() - entry.t <= maxAgeMs) {
    if (!refreshing.has(key)) {
      refreshing.add(key);
      Promise.resolve()
        .then(fetcher)
        .then((res) => (accept(res) ? writeEntry(key, res.data) : undefined))
        .catch(() => {}) // a failed refresh just leaves the stored answer to age out
        .finally(() => refreshing.delete(key));
    }
    return { data: entry.data, status: 200, fromCache: true };
  }

  const res = await fetcher();
  if (accept(res)) await writeEntry(key, res.data);
  return res;
}

/** Forget everything (Sync Menu, and tests). */
export async function clearDataCache() {
  memory.clear();
  try {
    const keys = await menuStore.keys();
    await Promise.all(keys.filter((k) => k.startsWith(PREFIX)).map((k) => menuStore.removeItem(k)));
  } catch {
    // nothing stored, or storage unavailable
  }
}
